import React, { useState } from "react";
import { FiLink, FiExternalLink, FiCopy, FiCheck } from "react-icons/fi";
import { LinkThreeData } from "../../types";
import utils from "../../helpers/utils";

interface LinksCardProps {
  data: LinkThreeData;
  onManageClick: () => void;
  previewUrl: string;
}

export default function LinksCard({
  data,
  onManageClick,
  previewUrl,
}: LinksCardProps) {
  const [copied, setCopied] = useState(false);
  const linkCount = data.buttons?.length || 0;
  const hasLinks = linkCount > 0;
  const additionalLinks = linkCount > 3 ? linkCount - 3 : 0;

  const fullUrl = previewUrl;

  const handleCopy = () => {
    if (!fullUrl) return;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border border-stroke bg-white shadow-sm transition-all hover:shadow-md dark:border-strokedark dark:bg-boxdark sm:rounded-xl">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-stroke px-4 py-4 dark:border-strokedark sm:gap-3 sm:px-6 sm:py-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30 sm:h-10 sm:w-10">
          <FiLink className="text-purple-600 dark:text-purple-400" size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-black dark:text-white text-sm sm:text-base">
            Meus Site
          </h3>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
            {linkCount}{" "}
            {linkCount === 1 ? "link cadastrado" : "links cadastrados"}
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4 sm:p-6 sm:space-y-6">
        {/* User Link Input */}
        {fullUrl && (
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              Seu Link Público
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border border-stroke bg-gray-50 px-3 py-2.5 text-xs text-gray-600 dark:border-strokedark dark:bg-meta-4 dark:text-gray-300 sm:px-4 sm:py-3 sm:text-sm">
                {fullUrl}
              </div>
              <button
                onClick={handleCopy}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-stroke bg-white text-gray-500 transition-colors hover:border-purple-300 hover:text-purple-600 dark:border-strokedark dark:bg-boxdark dark:text-gray-400 dark:hover:border-purple-600 dark:hover:text-purple-400 sm:h-11 sm:w-11"
                title="Copiar link"
              >
                {copied ? <FiCheck size={18} /> : <FiCopy size={18} />}
              </button>
            </div>
          </div>
        )}

        {/* Preview dos links */}
        {hasLinks ? (
          <div className="space-y-2.5 sm:space-y-3">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              Seu Botões de Link
            </label>
            {data.buttons.slice(0, 3).map((button, index) => (
              <div
                key={button.id}
                className="group flex items-center gap-2.5 rounded-lg border border-stroke bg-gray-50 p-3 transition-all hover:border-purple-300 hover:bg-purple-50/50 dark:border-strokedark dark:bg-meta-4 dark:hover:border-purple-700 dark:hover:bg-purple-900/10 sm:gap-3 sm:p-3.5"
              >
                {button.image && (
                  <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-stroke dark:border-strokedark sm:h-12 sm:w-12">
                    <img
                      src={utils.getUriImage(button.image)}
                      alt={button.label}
                      className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                )}
                <div className="flex-1 overflow-hidden min-w-0">
                  <p className="truncate text-sm font-semibold text-black dark:text-white">
                    {button.label}
                  </p>
                  {button.url && (
                    <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                      {button.url}
                    </p>
                  )}
                </div>
                <FiExternalLink
                  size={14}
                  className="shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500 sm:w-4 sm:h-4"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-stroke bg-gray-50 py-10 text-center dark:border-strokedark dark:bg-gray-800/50 sm:rounded-xl sm:py-12">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30 sm:h-16 sm:w-16">
              <FiLink
                size={24}
                className="text-purple-600 dark:text-purple-400 sm:w-7 sm:h-7"
              />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Nenhum link cadastrado
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Clique abaixo para adicionar seus links
            </p>
          </div>
        )}

        {/* Indicador de links adicionais */}
        {additionalLinks > 0 && (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-purple-50 px-3 py-2 dark:bg-purple-900/20 sm:px-4 sm:py-2.5">
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-purple-600 dark:bg-purple-400"></div>
              <div className="h-1.5 w-1.5 rounded-full bg-purple-600 dark:bg-purple-400"></div>
              <div className="h-1.5 w-1.5 rounded-full bg-purple-600 dark:bg-purple-400"></div>
            </div>
            <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">
              +{additionalLinks}{" "}
              {additionalLinks === 1 ? "link adicional" : "links adicionais"}
            </p>
          </div>
        )}

        {/* Botão de gerenciar */}
        <button
          onClick={onManageClick}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:gap-2.5 sm:px-6 sm:py-3.5"
        >
          <FiLink size={18} className="sm:w-5 sm:h-5" />
          Gerenciar Meu Site
        </button>
      </div>
    </div>
  );
}
